/**
 * VoiceButton — Microphone toggle with 3 visual states.
 *
 * States: idle (muted gray), listening (pulsing green), processing (amber).
 * 56px circle button with CSS keyframe pulsing animation.
 */
import { createElement, useId } from 'react';

export type VoiceState = 'idle' | 'listening' | 'processing';

interface VoiceButtonProps {
  state: VoiceState;
  onClick: () => void;
  disabled?: boolean;
}

const stateColors: Record<VoiceState, { bg: string; ring: string }> = {
  idle: { bg: 'var(--sw-bg-surface)', ring: 'var(--sw-border)' },
  listening: { bg: '#22c55e', ring: 'rgba(34, 197, 94, 0.4)' },
  processing: { bg: '#f59e0b', ring: 'rgba(245, 158, 11, 0.4)' },
};

const stateLabels: Record<VoiceState, string> = {
  idle: 'Start voice input',
  listening: 'Stop recording',
  processing: 'Processing...',
};

export function VoiceButton({ state, onClick, disabled }: VoiceButtonProps) {
  const id = useId();
  const styleId = `sw-voice-pulse-${id.replace(/:/g, '')}`;
  const colors = stateColors[state];
  const isPulsing = state === 'listening';

  return createElement(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: '8px',
      },
    },
    // Inject keyframe animation via style element
    isPulsing
      ? createElement('style', {
          dangerouslySetInnerHTML: {
            __html: `
              @keyframes ${styleId} {
                0% { box-shadow: 0 0 0 0 ${colors.ring}; }
                70% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0); }
                100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
              }
            `,
          },
        })
      : null,
    // Button
    createElement(
      'button',
      {
        type: 'button',
        onClick,
        disabled: disabled || state === 'processing',
        'aria-label': stateLabels[state],
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '56px',
          height: '56px',
          minWidth: '56px',
          borderRadius: '50%',
          border: `2px solid ${colors.ring}`,
          backgroundColor: colors.bg,
          color: state === 'idle' ? 'var(--sw-text-secondary)' : '#fff',
          cursor: disabled || state === 'processing' ? 'default' : 'pointer',
          padding: 0,
          animation: isPulsing ? `${styleId} 1.5s ease-in-out infinite` : 'none',
          transition: 'background-color 0.2s, border-color 0.2s',
          opacity: disabled ? 0.5 : 1,
        },
      },
      // Mic icon
      createElement(
        'svg',
        {
          width: 24,
          height: 24,
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        },
        createElement('path', { d: 'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' }),
        createElement('path', { d: 'M19 10v2a7 7 0 0 1-14 0v-2' }),
        createElement('line', { x1: 12, y1: 19, x2: 12, y2: 23 }),
        createElement('line', { x1: 8, y1: 23, x2: 16, y2: 23 }),
      ),
    ),
    // State label
    createElement(
      'span',
      {
        style: {
          fontSize: '12px',
          color: 'var(--sw-text-secondary)',
        },
      },
      stateLabels[state],
    ),
  );
}
